import React from 'react';
import HolidayCard from './HolidayCard';
import { formatCurrency, getTripTotals, getDaysUntil } from '../utils/helpers';

export default function HolidaysPage({ holidays, members, navigate, onAddPayment, onAddHoliday }) {
  const booked = holidays
    .filter(h => h.status === 'booked' || h.status === 'planning')
    .sort((a, b) => new Date(a.departureDate) - new Date(b.departureDate));
  const dreams = holidays.filter(h => h.status === 'dream');

  const hero = booked.find(h => getDaysUntil(h.departureDate) >= 0);

  const totalOutstanding = holidays.reduce((sum, h) => {
    const { outstanding } = getTripTotals(h);
    return sum + outstanding;
  }, 0);

  const nextPayment = holidays
    .flatMap(h => h.expenses.map(e => ({ ...e, destination: h.destination })))
    .filter(e => !e.isPaid && e.dueDate)
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0];

  const handleSelectHoliday = (id) => {
    navigate('holiday-detail', { holidayId: id });
  };

  return (
    <div className="animate-fade-in-up">
      {/* Summary stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-purple-100">
          <div className="text-sm text-purple-600 font-medium">Total Outstanding</div>
          <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-coral-500 bg-clip-text text-transparent">
            {formatCurrency(totalOutstanding)}
          </div>
          <div className="text-xs text-gray-400 mt-1">across {holidays.length} trip{holidays.length !== 1 ? 's' : ''}</div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-teal-100">
          <div className="text-sm text-teal-600 font-medium">Next Payment</div>
          {nextPayment ? (
            <>
              <div className="text-lg font-bold text-gray-800 truncate">{nextPayment.description}</div>
              <div className="text-sm text-gray-500">
                {formatCurrency(nextPayment.amount)} · {nextPayment.destination}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                Due in {getDaysUntil(nextPayment.dueDate)} days
              </div>
            </>
          ) : (
            <div className="text-lg font-bold text-green-500 mt-1">Nothing due! 🎉</div>
          )}
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-sunny-200">
          <div className="text-sm text-sunny-600 font-medium">Trips Planned</div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-gray-800">{booked.length}</span>
            <span className="text-sm text-gray-400">booked</span>
            {dreams.length > 0 && (
              <>
                <span className="text-3xl font-bold text-gray-800">+{dreams.length}</span>
                <span className="text-sm text-gray-400">dreaming</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add holiday button */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={onAddHoliday}
          className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-teal-500 hover:from-purple-600 hover:to-teal-600 text-white rounded-xl font-semibold transition-colors shadow-md shadow-purple-200"
        >
          + New Holiday
        </button>
      </div>

      {/* Coming Up section */}
      {booked.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-purple-500" />
            Coming Up
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {booked.map((h, i) => (
              <HolidayCard
                key={h.id}
                holiday={h}
                onSelect={handleSelectHoliday}
                onAddPayment={onAddPayment}
                isHero={hero && h.id === hero.id && i === 0}
                members={members}
              />
            ))}
          </div>
        </div>
      )}

      {/* Dream Trips section */}
      {dreams.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-sunny-400" />
            Dream Trips
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {dreams.map(h => (
              <HolidayCard
                key={h.id}
                holiday={h}
                onSelect={handleSelectHoliday}
                onAddPayment={onAddPayment}
                isHero={false}
                members={members}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {holidays.length === 0 && (
        <div className="text-center py-16">
          <div className="text-6xl mb-4">🌴</div>
          <h2 className="text-2xl font-bold text-gray-700 mb-2">No holidays yet!</h2>
          <p className="text-gray-500 mb-6">Start planning your next family adventure</p>
          <button
            onClick={onAddHoliday}
            className="px-8 py-3 bg-gradient-to-r from-purple-500 to-teal-500 text-white rounded-xl font-semibold text-lg shadow-lg shadow-purple-200 hover:from-purple-600 hover:to-teal-600 transition-colors"
          >
            Plan Your First Trip 🎉
          </button>
        </div>
      )}
    </div>
  );
}
